// Force-directed auto-layout for the ER diagram (dependency-free). Related tables
// (joined by foreign keys) attract; all tables repel, so the graph spreads into
// readable clusters. A final overlap-removal pass guarantees cards don't sit on
// top of each other. Deterministic seed (a grid) → a fresh layout is stable.
//
// layoutNodes(nodes, edges) where nodes = [{id, w, h}], edges = [{source,target}]
// (ids reference node ids) → { [id]: {x, y} } (top-left, padded to a 40px origin).

export function layoutNodes(nodes, edges, opts = {}) {
  const n = nodes.length;
  if (n === 0) return {};
  if (n === 1) return { [nodes[0].id]: { x: 40, y: 40 } };

  const iterations = opts.iterations ?? 350;
  const idToIdx = new Map(nodes.map((nd, i) => [nd.id, i]));

  // A target frame sized to the cards (with breathing room) BOUNDS the spread —
  // the critical bit: repulsion alone (few edges) would otherwise fling nodes
  // apart without limit. k = √(area/n) is the ideal node separation (classic FR).
  const avgW = nodes.reduce((s, nd) => s + nd.w, 0) / n;
  const avgH = nodes.reduce((s, nd) => s + nd.h, 0) / n;
  const area = n * (avgW + 90) * (avgH + 90) * 1.8;
  const W = Math.sqrt(area);
  const k = Math.sqrt(area / n);

  // Seed on a grid spread across the frame (deterministic → stable fresh layout).
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  const pos = nodes.map((_, i) => ({
    x: ((i % cols) + 0.5) / cols * W,
    y: ((Math.floor(i / cols)) + 0.5) / rows * W,
  }));

  const adj = edges
    .map((e) => [idToIdx.get(e.source), idToIdx.get(e.target)])
    .filter(([a, b]) => a != null && b != null && a !== b);

  let temp = W * 0.10;
  const tmin = W * 0.004;
  const cool = Math.pow(tmin / temp, 1 / iterations);

  for (let it = 0; it < iterations; it++) {
    const disp = pos.map(() => ({ x: 0, y: 0 }));
    // Repulsion between every pair (O(n²) — fine for typical schemas).
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const dx = pos[i].x - pos[j].x, dy = pos[i].y - pos[j].y;
        const d = Math.hypot(dx, dy) || 0.01;
        const f = (k * k) / d;
        const ux = dx / d, uy = dy / d;
        disp[i].x += ux * f; disp[i].y += uy * f;
        disp[j].x -= ux * f; disp[j].y -= uy * f;
      }
    }
    // Attraction along edges.
    for (const [a, b] of adj) {
      const dx = pos[a].x - pos[b].x, dy = pos[a].y - pos[b].y;
      const d = Math.hypot(dx, dy) || 0.01;
      const f = (d * d) / k;
      const ux = dx / d, uy = dy / d;
      disp[a].x -= ux * f; disp[a].y -= uy * f;
      disp[b].x += ux * f; disp[b].y += uy * f;
    }
    // Apply (capped by the cooling temperature), then CLAMP back into the frame.
    for (let i = 0; i < n; i++) {
      const dl = Math.hypot(disp[i].x, disp[i].y) || 0.01;
      pos[i].x += (disp[i].x / dl) * Math.min(dl, temp);
      pos[i].y += (disp[i].y / dl) * Math.min(dl, temp);
      pos[i].x = Math.max(0, Math.min(W, pos[i].x));
      pos[i].y = Math.max(0, Math.min(W, pos[i].y));
    }
    temp *= cool;
  }

  separate(nodes, pos, 36, 22);

  let minX = Infinity, minY = Infinity;
  for (const p of pos) { if (p.x < minX) minX = p.x; if (p.y < minY) minY = p.y; }
  const out = {};
  nodes.forEach((nd, i) => {
    out[nd.id] = { x: Math.round(pos[i].x - minX + 40), y: Math.round(pos[i].y - minY + 40) };
  });
  return out;
}

// separate pushes overlapping AABBs apart (with a gap) along their smaller-overlap
// axis — a few passes settle non-overlapping.
function separate(nodes, pos, gapX, gapY, passes = 60) {
  const n = nodes.length;
  for (let p = 0; p < passes; p++) {
    let moved = false;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = nodes[i], b = nodes[j];
        const acx = pos[i].x + a.w / 2, acy = pos[i].y + a.h / 2;
        const bcx = pos[j].x + b.w / 2, bcy = pos[j].y + b.h / 2;
        const ox = (a.w + b.w) / 2 + gapX - Math.abs(acx - bcx);
        const oy = (a.h + b.h) / 2 + gapY - Math.abs(acy - bcy);
        if (ox > 0 && oy > 0) {
          if (ox < oy) {
            const s = ((acx <= bcx ? -1 : 1) * ox) / 2;
            pos[i].x += s; pos[j].x -= s;
          } else {
            const s = ((acy <= bcy ? -1 : 1) * oy) / 2;
            pos[i].y += s; pos[j].y -= s;
          }
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
}
